import * as React from 'react';
import type { MapLanguage } from '@dx-ui/framework-uri-builder';
import { UriBuilder } from '@dx-ui/framework-uri-builder';
import { env } from '@dx-ui/framework-env';
import { useTranslation } from 'next-i18next';
import { MetricsEvent, useEventMetrics } from '../../hooks';
import type { GuestLoginResponse, LoginResponse } from '@dx-ui/osc-login';
import { LoginIFrame as Frame } from '@dx-ui/osc-login';
import { useAuth } from '@dx-ui/framework-auth-provider';
import { useRouter } from 'next/router';

const LoginFrame: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const language = router.locale;
  const { setEventMetrics } = useEventMetrics();
  const { login } = useAuth();

  const { forgetInfoLink, secureInfoLink } = React.useMemo(() => {
    const httpBuilder = new UriBuilder({
      language: language as MapLanguage,
      host: env('SECURE_HOST'),
    });
    return {
      forgetInfoLink: `${env('HONORS_SIGN_IN_URL')}forgot-information/`,
      secureInfoLink: httpBuilder.build('/policy/global-privacy-statement/index.html'),
    };
  }, [language]);

  const iframeSrc = React.useRef(
    `${env('DX_AUTH_UI').replace('/__LANG__/', `/${language}/`)}?t=${Date.now()}`
  ).current;

  const handleLoginError = React.useCallback(
    (error: GuestLoginResponse['error']) => {
      error?.status && setEventMetrics(MetricsEvent.LOG_IN_ERROR, { type: error?.status });
    },
    [setEventMetrics]
  );

  const handleLoginAttempt = React.useCallback(
    async ({ data, error }: LoginResponse) => {
      if (error) {
        handleLoginError(error);
      } else if (data) {
        await login({ data });
      }
    },
    [handleLoginError, login]
  );

  const handleClose = React.useCallback(() => undefined, []);

  return (
    <Frame
      id="hiltonLoginFrame"
      data-testid="loginIframe"
      frameSrc={iframeSrc}
      title={t('textContent.iframeTitle')}
      className="visible min-h-[27.5rem] min-w-[20.3125rem] max-w-[30rem] border-0 border-b border-b-surface"
      onLoginAttempt={handleLoginAttempt}
      onClose={handleClose}
      options={{
        forgetInfoLink: {
          url: forgetInfoLink,
        },
        secureInfoLink: {
          url: secureInfoLink,
        },
      }}
    />
  );
};

export default LoginFrame;
